import React, { useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Popconfirm, Table, Typography } from 'antd';
import { Catcher, SkyPaginationTotal } from '../../../';
import dayjs from 'dayjs';
import { appConfig } from '../../../../settings';
import { IAssetAssignment, IAssignmentScope } from '../../../../interfaces';
import {
    useCardSetFutureAssignments,
    useCardSetInfo,
    useCardSetParentAssignments,
    useUiIsSpinnerFound,
} from '../../../../hooks';
import { CARD_SET_MANAGE_PAGE_SIZE, cancelAssetAssignment, getCardSetAssignments } from '../../../../middleware';
import { tB, tF, tNM, tP, tT } from '../../../../translate';
import Styles from './m_styles.less';

interface IBlockingAssignmentsTable {
    scope: IAssignmentScope;
    onChange: () => void;
}

export const BlockingAssignmentsTable: React.FC<IBlockingAssignmentsTable> = observer(
    ({ scope, onChange }: IBlockingAssignmentsTable) => {
        const [sendingId, setSendingId] = useState('');
        const { id: cardSetId } = useCardSetInfo();
        const futureAssignments = useCardSetFutureAssignments();
        const parentAssignments = useCardSetParentAssignments();
        const isFuture = scope === 'future';
        const { list, total, pageNumber } = isFuture ? futureAssignments : parentAssignments;
        const isLoading = useUiIsSpinnerFound([
            isFuture ? 'GET_ASSET_ASSIGNMENTS_FUTURE_LIST' : 'GET_ASSET_ASSIGNMENTS_PARENT_BLOCKED_LIST',
        ]);

        if (!total) {
            return null;
        }

        const cancel = async (r: IAssetAssignment): Promise<void> => {
            setSendingId(r.id);
            try {
                const isDone = await cancelAssetAssignment(r.id);
                isDone && onChange();
            } finally {
                setSendingId('');
            }
        };

        const tableColumns = [
            { title: tF('Card Name'), dataIndex: 'assetName', ellipsis: true },
            { title: isFuture ? tF('Start Date') : tF('End Date'), dataIndex: 'date', width: 140 },
            { title: tF('Company'), dataIndex: 'companyName', ellipsis: true },
        ];
        if (isFuture) {
            tableColumns.push({ title: '', dataIndex: 'toolBar', width: 110 });
        }

        const formatDate = (date?: dayjs.Dayjs): string | undefined =>
            date?.isValid() ? date.format(appConfig.formatDate) : undefined;

        const tableData = list.map((r: IAssetAssignment) => ({
            key: r.id,
            assetName: r.assetName || r.assetId,
            date: formatDate(isFuture ? r.startDate : r.endDate),
            companyName: r.companyName,
            toolBar: isFuture ? (
                <Popconfirm
                    title={tNM('Cancel this assignment?')}
                    onConfirm={(): Promise<void> => cancel(r)}
                    okText={tB('Yes')}
                    cancelText={tB('No')}
                    placement="left"
                >
                    <Button danger size={'small'} loading={sendingId === r.id} disabled={!!sendingId}>
                        {tB('Cancel')}
                    </Button>
                </Popconfirm>
            ) : undefined,
        }));

        return (
            <Catcher>
                <div className={Styles.blockingTable}>
                    <Typography.Title level={5}>
                        {tP(isFuture ? 'Future Assignments' : 'Parent Assignments')} ({total})
                    </Typography.Title>
                    <Alert
                        type="info"
                        showIcon
                        message={tT('These assignments block the Reassign All and Cancel All actions.')}
                    />
                    <Table
                        dataSource={tableData}
                        columns={tableColumns}
                        loading={isLoading}
                        className={'table-theme sub-table'}
                        size={'small'}
                        pagination={
                            total > CARD_SET_MANAGE_PAGE_SIZE
                                ? {
                                      current: pageNumber,
                                      total,
                                      pageSize: CARD_SET_MANAGE_PAGE_SIZE,
                                      className: 'customPagination',
                                      showSizeChanger: false,
                                      size: 'default',
                                      showTotal: SkyPaginationTotal,
                                      onChange: (page: number): void => {
                                          getCardSetAssignments(cardSetId, scope, page);
                                      },
                                  }
                                : false
                        }
                    />
                </div>
            </Catcher>
        );
    },
);
